import React from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import { buildingStore } from "store/Building/BuildingStore";
import {
  IconCopy,
  IconEdit,
  IconInfoCircle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import ProductsListContainer from "../ProductsListContainer";
import { searchStore } from "features/ProductSearch/SearchStore";
import ProductRowsList from "../ProductRowsList";
import clsx from "clsx";
import EmptyState from "../EmptyState";
import ConfirmModal from "components/ConfirmModal";
import useToast from "hooks/useToast";
import { InternalProductSearchOutput, ResultOUT } from "api-client";
import { ROUTES_ENUM } from "routes/consts";
import ProductDetailsModal from "features/ProductDetailsModal";
import useComponentActions from "./useComponentActions";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";
import { AxiosError } from "axios";
import { ProductActionMode } from "utils";

export interface RemoveStateProps {
  open: boolean;
  id?: string | null;
  name?: string | null;
}

interface CustomProductsProps {
  match?: (product_id: string) => Promise<void>;
}

const CustomProducts = observer((props: CustomProductsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { saveStateAndGetPath } = useComponentActions();
  const [removeItemState, setRemoveItemState] = React.useState<
    RemoveStateProps | undefined
  >();

  React.useEffect(() => {
    buildingStore.getBuildingCalculationsStatus();
  }, []);

  function shouldShowEmptyView() {
    return (
      !searchStore.productSearchLoading &&
      searchStore.searchResults.items.length === 0
    );
  }

  const redirectToCreateProduct = () => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`);
  };

  function headerBar() {
    return (
      <div className="h-[66px]">
        {searchStore.searchResults.items.length ? (
          <div
            className={clsx(
              "flex justify-between p-3 items-center text-base rounded-md shadow-md relative",
              "bg-gradient-to-r from-teal-50 border border-gray-100"
            )}
          >
            <span className="font-normal text-gray-600 text-sm">
              {t("mapping.createBuildingProductMsg")}
            </span>
            {searchStore.canCreate && (
              <Button
                width="fit-content"
                type="primary"
                leadingIcon={<IconPlus />}
                onClick={redirectToCreateProduct}
              >
                {t("mapping.createBuildingProduct")}
              </Button>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  const editProduct = (id?: string | null) => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: { id: id, compliant: false, mode: ProductActionMode.Edit },
    });
  };

  const duplicateProduct = (id?: string | null) => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: { id: id, compliant: false, mode: ProductActionMode.Duplicate },
    });
  };

  const removeProduct = async () => {
    if (!removeItemState?.id) return;
    const filteredItems = searchStore.searchResults.items.filter(
      (item) => item.id !== removeItemState?.id
    );
    await BPEStore.deleteProduct(removeItemState?.id)
      .then(async () => {
        searchStore.setSearchResults({
          ...searchStore.searchResults,
          items: filteredItems,
        });
        toast(t("productEditor.successMsgOnDelete"), "success");
        setRemoveItemState(undefined);
      })
      .catch((err: AxiosError) => {
        const msg = `${
          ((err.response?.data as ResultOUT)?.message as ResultOUT)?.message ??
          err.message
        }`;
        toast(msg, "warning");
        console.error(err, "ProductEditorDeleteCustomBuildingProduct");
      });
  };

  const settingsItems = (
    product: InternalProductSearchOutput | null | undefined
  ) => {
    return [
      {
        name: (
          <div className="flex justify-between w-full">
            {t("mapping.editProduct")}
          </div>
        ),
        icon: <IconEdit width={18} className="mr-2" stroke={2} />,
        onClick: () => editProduct(product?.id),
        className: "text-indigo-700",
        disabled:
          buildingStore.calculationsStatus.inProgress ||
          product?.permissions?.can_edit === false,
      },
      {
        name: t("mapping.duplicateProduct"),
        icon: <IconCopy width={18} className="mr-2" stroke={2} />,
        onClick: () => duplicateProduct(product?.id),
        className: "text-indigo-700",
        disabled: product?.permissions?.can_duplicate === false,
      },
      {
        name: t("mapping.showDetails"),
        icon: <IconInfoCircle width={18} className="mr-2" stroke={2} />,
        onClick: () =>
          searchStore.setOpenDetails({ open: true, product: product }),
        className: "text-indigo-700",
        disabled: false,
      },
      {
        name: t("mapping.removeProduct"),
        icon: <IconTrash width={18} stroke={2} className="mr-2" />,
        onClick: () => {
          if (product?.is_part_of_a_component) {
            setRemoveItemState({
              open: true,
              id: null,
              name: null,
            });
          } else {
            setRemoveItemState({
              open: true,
              id: product?.id,
              name: product?.name,
            });
          }
        },
        className: "text-red-700",
        disabled:
          buildingStore.calculationsStatus.inProgress ||
          COEStore.openedSearchInCE ||
          product?.permissions?.can_delete === false,
        hidden: product?.permissions?.can_delete === false,
      },
    ];
  };

  return (
    <>
      {headerBar()}
      <ProductsListContainer className="!h-[calc(100vh-440px)] !mt-2">
        {shouldShowEmptyView() ? (
          <EmptyState />
        ) : (
          <ProductRowsList
            match={props.match}
            settingsItems={settingsItems}
            containerClassName="mt-5"
          />
        )}
      </ProductsListContainer>
      {removeItemState?.open && removeItemState?.id ? (
        <ConfirmModal
          type="warning"
          title={t("mapping.removeProductTitle", {
            name: removeItemState.name,
          })}
          open={removeItemState?.open}
          close={() => setRemoveItemState(undefined)}
          onConfirm={removeProduct}
          confirmButtonTitle={t("mapping.removeProductBtn", {
            name: t("mapping.product"),
          })}
          descriptionMesage={t("mapping.removeProductDesc")}
          containerClassName="!min-w-[600px]"
          buttonClassName="bg-red-600 hover:!bg-red-700"
        ></ConfirmModal>
      ) : null}
      {removeItemState?.open && !removeItemState.id ? (
        <ConfirmModal
          type="warning"
          title={t("mapping.removeProductForbidenTitle", {
            name: t("mapping.product"),
          })}
          open={removeItemState?.open}
          close={() => setRemoveItemState(undefined)}
          hideCancel
          onConfirm={() => setRemoveItemState(undefined)}
          confirmButtonTitle={t("mapping.closeNote")}
          descriptionMesage={t("productEditor.cannotDeleteBPMessage")}
          containerClassName="!min-w-[500px]"
        ></ConfirmModal>
      ) : null}
      <ProductDetailsModal
        productId={searchStore.openDetails.product?.id}
        productName={searchStore.openDetails.product?.name}
        productType={searchStore.openDetails.product?.type}
        open={searchStore.openDetails.open}
        close={() =>
          searchStore.setOpenDetails({ open: false, product: undefined })
        }
      />
    </>
  );
});

export default CustomProducts;
